import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../api";
import { useAuth } from "../context/AuthContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");

  const poll = useCallback(async (attempt) => {
    if (!sessionId) { setStatus("error"); return; }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus("paid");
        refresh && refresh();
        return;
      }
    } catch (e) { /* keep polling */ }
    if (attempt >= 8) { setStatus("pending"); return; }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId, refresh]);

  useEffect(() => { poll(0); }, [poll]);

  return (
    <div>
      <Header />
      <div className="hero-gradient py-20 px-5 min-h-[70vh]">
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-black/10 p-8 text-center shadow-[0_20px_50px_-20px_rgba(124,34,51,0.2)]">
          {status === "checking" && (
            <>
              <Loader2 className="w-10 h-10 text-[#7c2233] animate-spin mx-auto" />
              <h1 className="font-display text-2xl text-[#1f1a1b] mt-4">Confirming your membership…</h1>
              <p className="text-[15px] text-[#5a5254] mt-2">One moment please.</p>
            </>
          )}
          {(status === "paid" || status === "pending") && (
            <>
              <div className="w-14 h-14 mx-auto rounded-full bg-[#f7e4e6] flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-[#7c2233]" />
              </div>
              <h1 className="font-display text-2xl text-[#1f1a1b] mt-4">You're all set!</h1>
              <p className="text-[15px] text-[#5a5254] mt-2">
                Your membership is active (1 month free). Your job has been submitted — our team reviews it before it goes live. We'll email you once it's approved and when applications arrive.
              </p>
              <Link to="/dashboard" className="inline-flex mt-6 rounded-xl bg-[#7c2233] hover:bg-[#661b29] text-white font-medium px-6 py-3 transition-colors">
                Go to my dashboard
              </Link>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="w-12 h-12 text-[#7c2233] mx-auto" />
              <h1 className="font-display text-2xl text-[#1f1a1b] mt-4">Something went wrong</h1>
              <Link to="/dashboard" className="inline-flex mt-6 rounded-xl bg-[#7c2233] text-white font-medium px-6 py-3">Go to dashboard</Link>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div>
      <Header />
      <div className="hero-gradient py-20 px-5 min-h-[70vh]">
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-black/10 p-8 text-center">
          <XCircle className="w-12 h-12 text-[#8a8285] mx-auto" />
          <h1 className="font-display text-2xl text-[#1f1a1b] mt-4">Checkout canceled</h1>
          <p className="text-[15px] text-[#5a5254] mt-2">No worries — your job is saved as a draft. You can activate your membership anytime.</p>
          <Link to="/dashboard" className="inline-flex mt-6 rounded-xl bg-[#7c2233] hover:bg-[#661b29] text-white font-medium px-6 py-3 transition-colors">Go to dashboard</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}
